import { cloudinary, dateFormatInLocaleLanguage } from '@constants'
import { useLabelsFromAPI } from '@dmi-mch/hooks'
import { useEditorialModule } from '@hooks'
import { EditorialHero } from '@mch-group/uikit-components'
import { PageContext } from '@providers/PageContext'
import acceptTargetingCookies from '@utils/acceptTargetingCookies'
import isTargetingCookiesTypeAccepted from '@utils/isTargetingCookiesAccepted'
import dayjs from 'dayjs'
import { useContext } from 'react'
import customParseFormat from 'dayjs/plugin/customParseFormat'
dayjs.extend(customParseFormat)

const useEMHS = ({
  id,
  emhs_author,
  emhs_date,
  emhs_image_upload,
  emhs_reading_duration,
  emhs_social_share_action_button,
  emhs_subtitle,
  emhs_title,
  emhs_video_upload,
  emhs_type_of_story,
  emhs_video_position,
  emhs_youtube_video_url,
  emhs_background_media = 'image',
  emhs_background_video,
  emhs_duration_to_watch_lbl
}) => {
  const { page: { siteLanguage } } = useContext(PageContext)
  const { labels } = useLabelsFromAPI(['EditorialModules', 'AltTags', 'YoutubeCookieModal'], siteLanguage)
  /**
   * This object contains the functions that will be passed to the component of the Editorial Module Hero
   * @type {object}
   * @returns {object} - The functions props
   */
  const { getImageUrl, cleanTitle, cleanAllTags } = useEditorialModule()

  /**
   * This object contains the data that will be passed to the component of the Editorial Module Hero
   * @type {object}
   * @returns {object} - The data props
   */

  let affProps = { affiliation: 'none' } as any

  let urlVideo = emhs_video_upload

  if (urlVideo && emhs_type_of_story === 'video') {
    urlVideo = cloudinary.throughCloudfront(
      `https://res.cloudinary.com/${getEnvConfig.fe.cloudinary.name}/video/upload/sp_auto/${emhs_video_upload}.m3u8`
    )
  }

  if (emhs_type_of_story === 'youtubeVideo') {
    urlVideo = emhs_youtube_video_url
  }

  let urlPlaceholderVideo = emhs_background_video

  if (emhs_background_video) {
    urlPlaceholderVideo = cloudinary.throughCloudfront(
      `https://res.cloudinary.com/${getEnvConfig.fe.cloudinary.name}/video/upload/sp_auto/${emhs_background_video}.m3u8`
    )
  }

  let readingTime = `${emhs_reading_duration} ${!!labels ? labels['EditorialModules']?.minRead : 'min read'}`
  if (emhs_type_of_story === 'youtubeVideo' || emhs_type_of_story === 'video') {
    readingTime = `${emhs_duration_to_watch_lbl} ${!!labels ? labels['EditorialModules']?.minWatch : 'min watch'}`
  }

  const dateFEFormat = dateFormatInLocaleLanguage[siteLanguage]

  const dataProps = {
    a11yData: {
      shareBtn: labels?.AltTags?.generalShareToSocialMedia || 'Share to social media',
      addBtn: labels?.AltTags?.addToCollection || 'Add item to collection'
    },
    storyType: emhs_type_of_story === 'youtubeVideo' ? 'video' : emhs_type_of_story,
    author: emhs_author && `${!!labels ? labels['EditorialModules']?.by : 'by'} ${emhs_author}`,
    date: dayjs(emhs_date, 'DD/MM/YYYY HH:mm').format(dateFEFormat),
    heading: cleanTitle(emhs_title),
    imageAltText: cleanAllTags(emhs_title),
    readingTime,
    shareButton: emhs_social_share_action_button === 'true',
    shareButtonAction: ['facebook', 'whatsapp', 'linkedin', 'twitter', 'weebo', 'wechat', 'mail', 'link-default'],
    subheading: emhs_subtitle,
    ctaLabel: labels?.['EditorialModules']?.videoCTAText || 'Watch Now',
    layout: siteLanguage === 'zh_CN' ? 'chinese' : 'western' as any,
    affiliation: 'none' as any,
    mediaPosition: emhs_video_position,
    urlVideo,
    placeholderType: emhs_background_media,
    urlPlaceholderVideo,
    ...affProps
  }

  /**
   *
   * @param w - The width of the image
   * @param ar - The aspect ratio of the image
   * @returns {string} - The url of the image with the width and aspect ratio passed as parameters
   */
  const videoCoverImg = (w: number, ar: string) =>
    (emhs_video_upload || emhs_youtube_video_url) &&
      emhs_image_upload ? getImageUrl(emhs_image_upload, w, ar) : undefined

  const noCookiesModalProp: { noCookiesModal: IEditorialHero['noCookiesModal'] } = {
    noCookiesModal: emhs_type_of_story === 'youtubeVideo' ? {
      heading: labels?.YoutubeCookieModal?.modalTitle || '',
      description: labels?.YoutubeCookieModal?.modalDescription || '',
      ctaLabel: labels?.YoutubeCookieModal?.acceptAndWatchBtnLabel || '',
      isModalNeeded: (setCookiesAccepted) => {
        setCookiesAccepted(isTargetingCookiesTypeAccepted())
      },
      onClickAcceptCookies: () => {
        acceptTargetingCookies()
      }
    } : undefined
  }

  return {
    id,
    emhs_image_upload,
    videoCoverImg,
    getImageUrl,
    dataProps,
    noCookiesModalProp
  }
}

// Some TS magic to obtain the typings of the EditorialHero component
type IEditorialHero = React.ComponentProps<typeof EditorialHero>

export default useEMHS